import { useCallback, useMemo, useRef, useState } from 'react';
import { Download, FileAudio, Mic, MicOff, Trash2, Upload } from 'lucide-react';
import toast from 'react-hot-toast';
import AcousticCalibrationRadar from '../components/acousticCalibration/AcousticCalibrationRadar';
import AcousticCalibrationSnapshotList from '../components/acousticCalibration/AcousticCalibrationSnapshotList';
import AcousticCalibrationTagPanel from '../components/acousticCalibration/AcousticCalibrationTagPanel';
import AcousticThresholdPanel from '../components/acousticCalibration/AcousticThresholdPanel';
import { Button, Tile, TileContent, TileHeader, TileTitle } from '../components/ui';
import { useAcousticCalibrationStore } from '../hooks/useAcousticCalibrationStore';
import { useAcousticRoomAnalyser } from '../hooks/useAcousticRoomAnalyser';
import {
  averageSignals,
  buildCalibrationSnapshot
} from '../lib/acoustic/calibrationSnapshot';
import type { AcousticCalibrationTag } from '../lib/acoustic/calibrationTypes';
import { analyzeOfflineAudioFile, type OfflineAnalysisResult } from '../lib/acoustic/offlineAudioAnalysis';
import {
  clearCalibrationSnapshots,
  countSnapshotsByTag,
  exportCalibrationJson,
  importCalibrationJson
} from '../lib/acoustic/calibrationStorage';
import type { AcousticRoomSignal } from '../lib/liveLessonTypes';

const SNAPSHOT_AVERAGE_FRAMES = 8;

type InputMode = 'live' | 'file';

function situationLabel(key: string): string {
  switch (key) {
    case 'single_speaker':
      return 'Einzelsprecher';
    case 'conversation':
      return 'Dialog';
    case 'babble':
      return 'Gemurmel';
    case 'shouting':
      return 'Geschrei';
    case 'silence':
      return 'Stille';
    default:
      return 'Unklar';
  }
}

export default function AdminAcousticCalibration() {
  const [inputMode, setInputMode] = useState<InputMode>('live');
  const [audioEnabled, setAudioEnabled] = useState(false);
  const [note, setNote] = useState('');
  const [filterTag, setFilterTag] = useState<AcousticCalibrationTag | 'all'>('all');
  const [selectedSnapshotId, setSelectedSnapshotId] = useState<string | null>(null);
  const [fileAnalysis, setFileAnalysis] = useState<OfflineAnalysisResult | null>(null);
  const [fileProgress, setFileProgress] = useState<number | null>(null);
  const [isAnalyzingFile, setIsAnalyzingFile] = useState(false);
  const jsonImportRef = useRef<HTMLInputElement>(null);
  const audioFileRef = useRef<HTMLInputElement>(null);

  const { signal: liveSignal, history, isActive, isLoadingModels, error } = useAcousticRoomAnalyser({
    enabled: audioEnabled && inputMode === 'live'
  });
  const { snapshots, saveSnapshot, removeSnapshot, refresh } = useAcousticCalibrationStore();

  const activeSignal: AcousticRoomSignal | null =
    inputMode === 'file' ? fileAnalysis?.aggregate ?? null : liveSignal;

  const counts = useMemo(() => countSnapshotsByTag(snapshots), [snapshots]);

  const selectedSnapshot = useMemo(
    () => snapshots.find((entry) => entry.id === selectedSnapshotId) ?? null,
    [snapshots, selectedSnapshotId]
  );

  const meanTagSnapshots = useMemo(() => {
    if (filterTag === 'all') return null;
    const tagged = snapshots.filter((entry) => entry.tag === filterTag);
    return tagged.length ? tagged : null;
  }, [snapshots, filterTag]);

  const canTag =
    inputMode === 'file'
      ? Boolean(fileAnalysis?.aggregate)
      : Boolean(audioEnabled && isActive && !isLoadingModels);

  const handleTag = useCallback(
    (tag: AcousticCalibrationTag) => {
      if (!canTag) {
        toast.error(
          inputMode === 'file'
            ? 'Bitte zuerst eine Audiodatei analysieren.'
            : 'Bitte zuerst das Mikrofon starten.'
        );
        return;
      }

      let source: AcousticRoomSignal | null = null;
      if (inputMode === 'file') {
        source = fileAnalysis?.aggregate ?? null;
      } else {
        const recent = history.slice(-SNAPSHOT_AVERAGE_FRAMES);
        source = averageSignals(recent.length ? recent : liveSignal ? [liveSignal] : []);
      }

      if (!source) {
        toast.error('Noch kein Audiosignal – kurz warten und erneut taggen.');
        return;
      }

      const snapshot = buildCalibrationSnapshot(source, tag, {
        note,
        source: inputMode,
        sourceFileName: inputMode === 'file' ? fileAnalysis?.fileName : undefined
      });
      saveSnapshot(snapshot);
      setNote('');
      toast.success(`„${snapshot.tagLabel}" gespeichert`);
    },
    [canTag, inputMode, fileAnalysis, history, liveSignal, note, saveSnapshot]
  );

  const handleAudioFile = async (file: File) => {
    const lower = file.name.toLowerCase();
    if (!/\.(wav|mp3|m4a|ogg|webm|flac|aac)$/.test(lower)) {
      toast.error('Bitte WAV, MP3 oder ein anderes gängiges Audioformat wählen.');
      return;
    }
    setInputMode('file');
    setAudioEnabled(false);
    setIsAnalyzingFile(true);
    setFileProgress(0);
    setFileAnalysis(null);
    try {
      const result = await analyzeOfflineAudioFile(file, (ratio) => setFileProgress(ratio));
      setFileAnalysis(result);
      toast.success(
        `„${file.name}" analysiert (${result.frameCount} Frames, ${result.durationSec.toFixed(1)} s)`
      );
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Analyse fehlgeschlagen';
      toast.error(message);
    } finally {
      setIsAnalyzingFile(false);
      setFileProgress(null);
    }
  };

  const handleExport = () => {
    const blob = new Blob([exportCalibrationJson()], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const anchor = document.createElement('a');
    anchor.href = url;
    anchor.download = `audio-kalibrierung-${new Date().toISOString().slice(0, 10)}.json`;
    anchor.click();
    URL.revokeObjectURL(url);
    toast.success('Export gestartet');
  };

  const handleImport = (file: File) => {
    const reader = new FileReader();
    reader.onload = () => {
      const text = typeof reader.result === 'string' ? reader.result : '';
      const result = importCalibrationJson(text);
      if (!result.ok) {
        toast.error(result.error);
        return;
      }
      toast.success(`${result.count} Snapshots importiert`);
      refresh();
      setSelectedSnapshotId(null);
    };
    reader.readAsText(file);
  };

  const handleClearAll = () => {
    if (!window.confirm('Alle Kalibrierungs-Snapshots unwiderruflich löschen?')) return;
    clearCalibrationSnapshots();
    refresh();
    setSelectedSnapshotId(null);
    toast.success('Alle Snapshots gelöscht');
  };

  return (
    <div className="mx-auto max-w-6xl space-y-6 p-4 md:p-6">
      <header className="space-y-2">
        <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100">Audio-Kalibrierung</h1>
        <p className="text-sm text-gray-600 dark:text-gray-400">
          Live-Mikrofon oder Audiodatei (WAV/MP3). Zwei Netzdiagramme zeigen Analyseparameter und die
          Werte, die Lernrakete für YAMNet, Fusion und die Situationsentscheidung nutzt.
        </p>
      </header>

      <div className="flex flex-wrap items-center gap-2">
        <Button
          variant={inputMode === 'live' && audioEnabled ? 'primary' : 'outline'}
          onClick={() => {
            setInputMode('live');
            setAudioEnabled((prev) => !prev);
          }}
        >
          {audioEnabled && inputMode === 'live' ? (
            <Mic className="mr-2 h-4 w-4" />
          ) : (
            <MicOff className="mr-2 h-4 w-4" />
          )}
          Mikrofon {audioEnabled && inputMode === 'live' ? 'an' : 'aus'}
        </Button>
        <Button
          variant="outline"
          disabled={isAnalyzingFile}
          onClick={() => audioFileRef.current?.click()}
        >
          <FileAudio className="mr-2 h-4 w-4" />
          {isAnalyzingFile ? 'Analysiere …' : 'Audio-Datei'}
        </Button>
        <input
          ref={audioFileRef}
          type="file"
          accept="audio/*,.wav,.mp3,.m4a,.ogg,.webm,.flac"
          className="hidden"
          onChange={(event) => {
            const file = event.target.files?.[0];
            if (file) void handleAudioFile(file);
            event.target.value = '';
          }}
        />
        <Button variant="outline" onClick={handleExport} disabled={!snapshots.length}>
          <Download className="mr-2 h-4 w-4" />
          Export JSON
        </Button>
        <Button variant="outline" onClick={() => jsonImportRef.current?.click()}>
          <Upload className="mr-2 h-4 w-4" />
          Snapshots importieren
        </Button>
        <input
          ref={jsonImportRef}
          type="file"
          accept="application/json,.json"
          className="hidden"
          onChange={(event) => {
            const file = event.target.files?.[0];
            if (file) handleImport(file);
            event.target.value = '';
          }}
        />
        <Button variant="outline" onClick={handleClearAll} disabled={!snapshots.length}>
          <Trash2 className="mr-2 h-4 w-4" />
          Alle löschen
        </Button>
      </div>

      {fileProgress !== null ? (
        <div className="space-y-1">
          <div className="h-2 overflow-hidden rounded-full bg-gray-200 dark:bg-gray-700">
            <div
              className="h-full rounded-full bg-sky-500 transition-all"
              style={{ width: `${Math.round(fileProgress * 100)}%` }}
            />
          </div>
          <p className="text-xs text-gray-500">Datei wird durch die Lernrakete-Pipeline analysiert …</p>
        </div>
      ) : null}

      {error && inputMode === 'live' ? (
        <p className="rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-800 dark:border-red-900 dark:bg-red-950/50 dark:text-red-200">
          {error}
        </p>
      ) : null}
      {isLoadingModels && inputMode === 'live' ? (
        <p className="text-sm text-gray-500">YAMNet-Modelle werden geladen …</p>
      ) : null}

      {fileAnalysis ? (
        <p className="text-sm text-gray-600 dark:text-gray-400">
          Datei: <span className="font-medium">{fileAnalysis.fileName}</span> ·{' '}
          {fileAnalysis.durationSec.toFixed(1)} s · {fileAnalysis.frameCount} Frames · Auto:{' '}
          <span className="font-medium">
            {situationLabel(fileAnalysis.aggregate.detectedSituation)} (
            {Math.round(fileAnalysis.aggregate.situationConfidence * 100)} %)
          </span>
        </p>
      ) : null}

      <div className="grid gap-6 xl:grid-cols-[1.4fr_1fr]">
        <Tile>
          <TileHeader>
            <TileTitle>
              {inputMode === 'file' ? 'Parameter & Entscheidung (Datei)' : 'Parameter & Entscheidung (Live)'}
            </TileTitle>
          </TileHeader>
          <TileContent className="space-y-3">
            <AcousticCalibrationRadar
              signal={activeSignal}
              referenceSnapshot={selectedSnapshot}
              meanTagSnapshots={meanTagSnapshots}
            />
            <div className="grid grid-cols-2 gap-2 text-xs text-gray-600 dark:text-gray-400">
              <div>
                <span className="text-gray-500">Auto-Erkennung</span>
                <p className="font-medium text-gray-800 dark:text-gray-200">
                  {activeSignal ? situationLabel(activeSignal.detectedSituation) : '—'}
                  {activeSignal ? ` (${Math.round(activeSignal.situationConfidence * 100)}%)` : ''}
                </p>
              </div>
              <div>
                <span className="text-gray-500">Pegel</span>
                <p className="font-medium text-gray-800 dark:text-gray-200">
                  {activeSignal ? `${activeSignal.levelDb.toFixed(1)} dB` : '—'}
                </p>
              </div>
              <div>
                <span className="text-gray-500">Quelle</span>
                <p className="font-medium text-gray-800 dark:text-gray-200">
                  {inputMode === 'file' ? fileAnalysis?.fileName ?? 'Datei' : 'Mikrofon'}
                </p>
              </div>
              <div>
                <span className="text-gray-500">Phase</span>
                <p className="font-medium text-gray-800 dark:text-gray-200">{activeSignal?.phaseFit ?? '—'}</p>
              </div>
            </div>
          </TileContent>
        </Tile>

        <Tile>
          <TileHeader>
            <TileTitle>Situation taggen</TileTitle>
          </TileHeader>
          <TileContent className="space-y-4">
            <label className="block text-sm">
              <span className="mb-1 block text-gray-600 dark:text-gray-400">Notiz (optional)</span>
              <input
                type="text"
                value={note}
                onChange={(event) => setNote(event.target.value)}
                placeholder="z. B. Gruppe 7a, Fenster offen"
                className="w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm dark:border-gray-600 dark:bg-gray-900"
              />
            </label>
            <p className="text-xs text-gray-500">
              {inputMode === 'file'
                ? 'Es wird der Mittelwert der gesamten Datei-Analyse gespeichert.'
                : `Live: Mittel der letzten ${SNAPSHOT_AVERAGE_FRAMES} Frames.`}
            </p>
            <AcousticCalibrationTagPanel counts={counts} disabled={!canTag} onTag={handleTag} />
          </TileContent>
        </Tile>
      </div>

      <Tile>
        <TileHeader>
          <TileTitle>Gespeicherte Snapshots ({snapshots.length})</TileTitle>
        </TileHeader>
        <TileContent className="space-y-6">
          <AcousticThresholdPanel snapshots={snapshots} filterTag={filterTag} />
          <AcousticCalibrationSnapshotList
            snapshots={snapshots}
            filterTag={filterTag}
            selectedId={selectedSnapshotId}
            onFilterTag={setFilterTag}
            onSelect={setSelectedSnapshotId}
            onDelete={(id) => {
              removeSnapshot(id);
              if (selectedSnapshotId === id) setSelectedSnapshotId(null);
              toast.success('Snapshot gelöscht');
            }}
          />
        </TileContent>
      </Tile>
    </div>
  );
}
